import {
  discriminate,
  EngagementReportItem,
  UserField,
  UserFieldType,
  UserFilter,
} from "@coaching-culture/types";
import {
  Button,
  DateRangeButton,
  Flex,
  Loader,
  Panel,
  SortableTable,
  SortableTableColumn,
  UserFlag,
} from "@coaching-culture/ui";
import { sortBy } from "lodash";
import { useCallback, useMemo, useState } from "react";

import {
  useEngagementReport,
  useUserFields,
} from "@coaching-culture/api-client";
import { useGoalTerminology } from "auth/OrgProvider";
import CenterColumn from "components/CenterColumn";
import { FilterModal } from "components/FilterPanel";
import { PageHeader } from "components/PageHeader";
import { addDays } from "date-fns";
import { Link } from "react-router-dom";
import { saveExportAsCsv } from "utils";

export const EngagementInsights = () => {
  const goalTerms = useGoalTerminology();
  const { data: userFields } = useUserFields();
  const [filter, setFilter] = useState<UserFilter>({
    groups: [],
    userFields: [],
  });
  const [range, setRange] = useState<[Date, Date]>([
    addDays(new Date(), -30),
    new Date(),
  ]);
  const report = useEngagementReport(range[0], range[1], filter);

  const [showFilters, setShowFilters] = useState(false);

  const formatUserField = useCallback(
    (user: EngagementReportItem, userFieldDef: UserField) => {
      const selectFields = (userFields || []).filter(
        discriminate("type", UserFieldType.Select),
      );

      const field = user.userFields.find((uf) => uf.id === userFieldDef.id);
      if (field != null) {
        if (userFieldDef.type === UserFieldType.Select) {
          const selectField = selectFields.find(
            (f) => f.id === userFieldDef.id,
          );
          return (
            selectField.options.find((option) => option.id === field.value)
              ?.name ?? ""
          );
        } else {
          return field.value as string;
        }
      }
      return "";
    },
    [userFields],
  );

  const getUserFieldCols = useMemo(() => {
    return (
      userFields?.map((y) => ({
        label: y.name,
        headingProps: {
          style: {
            textAlign: "center",
            whiteSpace: "nowrap",
          },
        },
        name: y.id,
        props: {
          style: {
            textAlign: "center",
          },
        },
        sortFormat: (x: EngagementReportItem) => {
          return formatUserField(x, y);
        },
        format: (x: EngagementReportItem) => {
          return formatUserField(x, y);
        },
        exportFormat: (x: EngagementReportItem) => {
          return formatUserField(x, y);
        },
      })) ?? []
    );
  }, [userFields, formatUserField]);

  const cols = useMemo(
    (): SortableTableColumn<EngagementReportItem>[] => [
      {
        name: "name",
        label: "Name",
        format: (x) => (
          <UserFlag user={x} to={`/success/people/users/${x.id}`} />
        ),
        sortFormat: (x) => x.name.toLowerCase(),
        exportFormat: (x) => x.name,
      },
      {
        name: "managers",
        label: "Managers",
        format: (x) => (
          <Flex flexDirection={"column"} style={{ gap: 3 }}>
            {x.managers.map((x) => (
              <UserFlag user={x} />
            ))}
          </Flex>
        ),
        sortFormat: (x) => x.managers[0]?.name ?? "",
        exportFormat: (x) => x.managers.map((x) => x.name).join(", "),
      },
      {
        name: "groups",
        label: "Groups",
        format: (x) =>
          sortBy(x.groups, "id").map((x, i, self) => (
            <>
              <Link key={x.id} to={`/success/people/groups/${x.id}`}>
                {x.name}
              </Link>
              {i < self.length - 1 && " | "}
            </>
          )),
        exportFormat: (x) => x.groups.map((x) => x.name).join(" | "),
        sortFormat: (x) => x.groups.map((x) => x.name).join(" | "),
      },
      ...getUserFieldCols,
      {
        name: "mindsetCompleted",
        label: "Mindset Completed",
        numeric: true,
      },
      {
        name: "lessonsCompleted",
        label: "Lessons Completed",
        numeric: true,
      },
      {
        name: "assessmentsCompleted",
        label: "Assessments Completed",
        numeric: true,
      },
      {
        name: "learnCastCompleted",
        label: "LearnCast Completed",
        numeric: true,
      },
      {
        name: "conversationsScheduled",
        label: "Conversations Scheduled",
        numeric: true,
      },
      {
        name: "conversationsCompleted",
        label: "Conversations Completed",
        numeric: true,
      },
      {
        name: "conversationsCancelled",
        label: "Conversations Cancelled",
        numeric: true,
      },
      {
        name: "goalsSet",
        label: `${goalTerms.goal.asPluralTitle()} Set`,
        numeric: true,
      },
      {
        name: "goalsCompleted",
        label: `${goalTerms.goal.asPluralTitle()} Completed`,
        numeric: true,
      },
    ],
    [goalTerms, getUserFieldCols],
  );

  const data = report.data;

  const onCancel = (result: UserFilter) => {
    if (result) {
      setFilter(result);
    }

    setShowFilters(false);
  };

  return (
    <CenterColumn>
      <PageHeader
        text="Engagement"
        subtitle="A report on the general engagement of the platform"
      >
        <Flex mb={2} justifyContent={"flex-end"}>
          <Button color="primary" onClick={() => setShowFilters(true)} mr={2}>
            Filters ({filter.groups.length + filter.userFields.length})
          </Button>
          <DateRangeButton value={range} onChange={setRange} noNull />
        </Flex>
      </PageHeader>
      <FilterModal
        isOpen={showFilters}
        filter={filter}
        filteredUsers={(data ?? []).map((x) => x.id)}
        onClose={(result) => {
          onCancel(result);
        }}
      />

      <Panel style={{ overflow: "hidden", position: "relative" }}>
        {report.isLoading || (report.isPreviousData && <Loader overlay />)}
        <SortableTable
          columns={cols}
          data={report.data}
          defaultSort={1}
          rowKey="id"
          paged
          onExport={saveExportAsCsv("EngagementReport.csv")}
        />
      </Panel>
    </CenterColumn>
  );
};
